<template>
    <div class="">
        <!-- Search Bar -->
        <div class="mb-1 d-flex justify-content-end row">
            <div class="col-md-3 mb-1">
                <div class="dropdown">
                    <button class="btn btn-outline-secondary dropdown-toggle w-100" type="button"
                            data-bs-toggle="dropdown" aria-expanded="false">
                        <i class="fa fa-columns me-2"></i>Columns
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end p-2">
                        <li class="mb-2">
                            <div class="d-flex justify-content-between">
                                <button class="btn btn-sm btn-outline-primary" @click="selectAllColumns">
                                    Select All
                                </button>
                                <button class="btn btn-sm btn-outline-secondary" @click="deselectAllColumns">
                                    Deselect All
                                </button>
                            </div>
                        </li>
                        <li><hr class="dropdown-divider"></li>
                        <li v-for="header in headers" :key="header.value" class="mb-1">
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" 
                                       :id="'col-' + header.value"
                                       v-model="visibleColumns[header.value]"
                                       @change="saveColumnPreferences">
                                <label class="form-check-label" :for="'col-' + header.value">
                                    {{ header.text }}
                                </label>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="col-md-3 mb-1">
                <input v-model="startdateQuery" class="form-control mx-1" type="datetime-local">
            </div>
            <div class="col-md-3 mb-1">
                <input v-model="enddateQuery" class="form-control mx-1" type="datetime-local">
            </div>
            <div class="col-md-3 mb-1">
                <div class="input-group">
                    <input type="text" class="form-control" placeholder="Search..." v-model="searchQuery"
                        @keyup.enter="handleSearch">
                    <button class="btn btn-primary" type="button" @click="handleSearch">
                        <i class="fa fa-search"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Table -->
        <div class="table-responsive table-freeze">
            <table class="table table-sm table-hover theme-scrollbar" style="margin-top: 2px !important;">
                <thead class="table-dark">
                    <tr>
                        <!-- {{ Conditionally render select-all checkbox based on selectable and multiSelectable }} -->
                        <!-- <th v-if="selectable" class="align-middle text-center freeze-column">
                            <input v-if="multiSelectable" type="checkbox" class="form-check-input"
                                :checked="isAllSelected" @change="toggleSelectAll">
                        </th> -->
                        <th class="align-middle freeze-column">No</th>
                        <th v-for="(header, index) in visibleHeaders" :key="header.value" 
                            :class="['align-middle header-wrap', { 'freeze-column': header.freeze }]"
                            :style="header.freeze ? { left: getFreezeColumnLeft(index) + 'px' } : {}">
                            {{ header.text }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="loading">
                        <!-- {{ Adjust colspan based on whether the select column is visible }} -->
                        <td :colspan="visibleHeaders.length + (selectable ? 2 : 1)" class="text-center p-3">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </td>
                    </tr>
                    <tr v-else-if="!safeItems.length">
                        <!-- {{ Adjust colspan based on whether the select column is visible }} -->
                        <td :colspan="visibleHeaders.length + (selectable ? 2 : 1)" class="text-center p-3">
                            <i class="fa fa-info-circle me-2"></i>No data available
                        </td>
                    </tr>
                    <tr v-else v-for="(item, index) in safeItems" :key="item.ID"
                        :class="{ 'table-active': selectable && selectedItems.includes(item.ID) }"
                        @click="selectable ? toggleSelect(item.ID) : null"
                        :style="{ cursor: selectable ? 'pointer' : 'default' }">
                        <!-- {{ Conditionally render row checkbox based on selectable }} -->
                        <!-- <td v-if="selectable" class="text-center freeze-column" style="left: 0px;">
                            <input :type="multiSelectable ? 'checkbox' : 'radio'" class="form-check-input"
                                :checked="selectedItems.includes(item.ID)"
                                :name="multiSelectable ? undefined : 'singleSelectGroup'" @click.stop
                                @change="toggleSelect(item.ID)">
                        </td> -->
                        <td class="text-center freeze-column">{{ startIndex + index }}</td>
                        <td v-for="(header, headerIndex) in visibleHeaders" :key="header.value" 
                            :class="['align-middle header-wrap', { 'freeze-column': header.freeze }]"
                            :style="header.freeze ? { left: getFreezeColumnLeft(headerIndex) + 'px' } : {}">
                            <slot :name="`item.${header.value}`" :item="item">
                                {{ item[header.value] }}
                            </slot>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Footer -->
        <div class="d-flex justify-content-between align-items-center mt-2">
            <div class="d-flex align-items-center">
                <span class="me-2">size:</span>
                <select class="form-select" v-model="localParams.size" @change="updateParams">
                    <option v-for="size in [5, 10, 50, 100, 500]" :key="size" :value="size">
                        {{ size }}
                    </option>
                </select>
            </div>
            <div class="d-flex align-items-center">
                <span class="me-3">
                    {{ startIndex }}-{{ endIndex }} of {{ total }}
                </span>
                <nav>
                    <ul class="pagination mb-0">
                        <li class="page-item" :class="{ disabled: localParams.page === 1 }">
                            <button class="page-link" @click="previousPage">
                                Previous
                            </button>
                        </li>
                        <li class="page-item" :class="{ disabled: endIndex >= total }">
                            <button class="page-link" @click="nextPage">
                                Next
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue'
// @ts-ignore
import debounce from 'lodash/debounce'

onMounted(() => {
    initializeColumnVisibility()
    handleSearch()
})

const props = defineProps<{
    headers: { text: string, value: string, [key: string]: any }[],
    items: { ID: any, [key: string]: any }[] | null, // allow null
    loading?: boolean,
    total: number,
    params: { page: number, size: number, start_date?: string, end_date?: string, search?: string, [key: string]: any },
    selectable?: boolean,
    multiSelectable?: boolean,
    tableId?: string, // Unique identifier for saving column preferences
}>()

// Add this computed property to safely handle null items
const safeItems = computed(() => props.items ?? [])

const emit = defineEmits(['update:params', 'selection-change'])

const searchQuery = ref(props.params.search || '') // Initialize with prop value
const startdateQuery = ref(props.params.start_date || new Date().toISOString().split('T')[0] + " 00:00") // Initialize with prop value
const enddateQuery = ref(props.params.end_date || new Date().toISOString().split('T')[0] + " 23:59") // Initialize with prop value
const localParams = ref({ ...props.params })
// Column visibility management
const visibleColumns = ref<Record<string, boolean>>({})
const tableIdentifier = computed(() => props.tableId || 'default-table')

// Initialize column visibility
const initializeColumnVisibility = () => {
    const savedPreferences = localStorage.getItem(`table-columns-${tableIdentifier.value}`)
    if (savedPreferences) {
        try {
            const preferences = JSON.parse(savedPreferences)
            visibleColumns.value = { ...preferences }
        } catch (e) {
            console.warn('Failed to parse saved column preferences')
        }
    }
    
    // Ensure all headers have visibility state (default to true)
    props.headers.forEach(header => {
        if (!(header.value in visibleColumns.value)) {
            visibleColumns.value[header.value] = true
        }
    })
}

// Computed property for visible headers
const visibleHeaders = computed(() => {
    return props.headers.filter(header => visibleColumns.value[header.value])
})

// Save column preferences to localStorage
const saveColumnPreferences = () => {
    localStorage.setItem(`table-columns-${tableIdentifier.value}`, JSON.stringify(visibleColumns.value))
}

// Select all columns
const selectAllColumns = () => {
    props.headers.forEach(header => {
        visibleColumns.value[header.value] = true
    })
    saveColumnPreferences()
}

// Deselect all columns
const deselectAllColumns = () => {
    props.headers.forEach(header => {
        visibleColumns.value[header.value] = false
    })
    saveColumnPreferences()
}

const startIndex = computed(() => {
    return (localParams.value.page - 1) * localParams.value.size + 1
})

const endIndex = computed(() => {
    return Math.min(startIndex.value + localParams.value.size - 1, props.total)
})

const handleSearch = debounce(() => {
    localParams.value = {
        ...localParams.value,
        start_date: startdateQuery.value,
        end_date: enddateQuery.value,
        page: 1,
        search: searchQuery.value
    }
    updateParams()
}, 300)

const previousPage = () => {
    if (localParams.value.page > 1) {
        localParams.value.page--
        updateParams()
    }
}

const nextPage = () => {
    if (endIndex.value < props.total) {
        localParams.value.page++
        updateParams()
    }
}

const updateParams = () => {
    let paramsTemp = { ...localParams.value }
    paramsTemp.start_date = paramsTemp.start_date ? new Date(paramsTemp.start_date).toLocaleString('sv-SE', { timeZone: 'Asia/Jakarta' }).replace(' ', 'T') : undefined
    paramsTemp.end_date = paramsTemp.end_date ? new Date(paramsTemp.end_date).toLocaleString('sv-SE', { timeZone: 'Asia/Jakarta' }).replace(' ', 'T') : undefined
    emit('update:params', { ...paramsTemp })
}


watch(() => props.params, (newParams) => {
    localParams.value = { ...newParams }
    // Update search query only if it differs to avoid resetting user input during debounce
    if (searchQuery.value !== (newParams.search || '')) {
        searchQuery.value = newParams.search || ''
    }
}, { deep: true })

const selectedItems = ref<Array<any>>([])

const isAllSelected = computed(() => {
    // All selected only makes sense for multi-select
    // @ts-ignore
    return props.selectable && props.multiSelectable && props.items.length > 0 && selectedItems.value.length === props.items.length
})

// {{ Modify toggleSelect based on multiSelectable }}
const toggleSelect = (id: any) => {
    if (!props.selectable) return; // Do nothing if not selectable

    if (props.multiSelectable) {
        // Multi-select logic (existing)
        const index = selectedItems.value.indexOf(id);
        if (index === -1) {
            selectedItems.value.push(id);
        } else {
            selectedItems.value.splice(index, 1);
        }
    } else {
        // Single-select logic
        if (selectedItems.value.includes(id)) {
            // If clicking the already selected item, deselect it
            selectedItems.value = [];
        } else {
            // Select the new item (replacing any previous selection)
            selectedItems.value = [id];
        }
    }
    emit('selection-change', selectedItems.value);
}


// {{ Modify toggleSelectAll to only work if multiSelectable }}
const toggleSelectAll = () => {
    // Only allow select all if multi-select is enabled
    if (!props.selectable || !props.multiSelectable) return;

    if (isAllSelected.value) {
        selectedItems.value = [];
    } else {
        // @ts-ignore
        selectedItems.value = props.items.map(item => item.ID);
    }
    emit('selection-change', selectedItems.value);
}


watch(() => props.items, () => {
    // Filter out selected items that are no longer present in the current items list
    selectedItems.value = selectedItems.value.filter(id =>
        // @ts-ignore
        props.items.some(item => item.ID === id)
    )
    // If not multi-selectable, ensure only max one item remains selected after items change
    if (props.selectable && !props.multiSelectable && selectedItems.value.length > 1) {
        selectedItems.value = [selectedItems.value[0]]; // Keep only the first one found
        emit('selection-change', selectedItems.value); // Emit change if corrected
    }
}, { deep: true })

// {{ Ensure selection is cleared or reduced to single if multiSelectable changes }}
watch(() => props.multiSelectable, (isMulti) => {
    if (props.selectable) {
        if (!isMulti && selectedItems.value.length > 1) {
            // If switching to single select and multiple are selected, keep only the first
            selectedItems.value = [selectedItems.value[0]];
            emit('selection-change', selectedItems.value);
        }
        // No action needed if switching to multi-select or if selection is already valid
    }
});

// {{ Ensure selection is cleared if selectable changes to false }}
watch(() => props.selectable, (isSelectable) => {
    if (!isSelectable && selectedItems.value.length > 0) {
        selectedItems.value = [];
        emit('selection-change', selectedItems.value);
    }
});

// Calculate left position for freeze columns
const getFreezeColumnLeft = (headerIndex: number) => {
    let leftPosition = props.selectable ? 0 : 0; // Base position after checkbox and number columns
    
    for (let i = 0; i < headerIndex; i++) {
        if (visibleHeaders.value[i].freeze) {
            leftPosition += visibleHeaders.value[i].width || 0; // Default width 100px if not specified
        }
    }
    
    return leftPosition;
};

// Watch for changes in headers prop to reinitialize column visibility
watch(() => props.headers, () => {
    initializeColumnVisibility()
}, { deep: true })

</script>

<style scoped>
.datatable {
    width: 100%;
    overflow-x: auto;
    /* Add horizontal scrolling */
}

.table-freeze {
    position: relative;
}

.freeze-column {
    position: sticky;
    z-index: 10;
    background-color: inherit;
    border-right: 2px solid #dee2e6;
}

.freeze-column.table-dark,
thead .freeze-column {
    background-color: var(--bs-dark) !important;
}

tbody .freeze-column {
    background-color: white;
}

tbody tr:hover .freeze-column {
    background-color: var(--bs-table-hover-bg);
}

tbody tr.table-active .freeze-column {
    background-color: var(--bs-table-active-bg);
}

.table-active {
    --bs-table-accent-bg: var(--bs-table-active-bg);
    color: var(--bs-table-active-color);
}

.header-wrap {
    white-space: nowrap;
    /* Allow text wrapping */
    word-wrap: break-word;
    /* Break words if necessary */
}

/* {{ Style radio buttons like checkboxes for consistency if desired }} */
/* input[type="radio"].form-check-input {
    border-radius: 0.25em; // Make radios square-ish
} */
</style>